import { baseAudioType } from '../audioNotes';
import { putObject } from '../uploadPhotos';

/*
 * What a chat message carries beside its text (routes/chat.py, migration
 * 0093; docs/FIELD_AI_VISION_SPEC.md Part VI, phase D1): photographs, voice
 * notes, places sent on purpose and references to records.
 *
 * A draft attachment, as the composer and the outbox keep it:
 *
 *   { id, kind: 'image', blob, thumb, media_type, width, height, copy? }
 *   { id, kind: 'audio', blob, media_type, duration_s, recorded_via }
 *   { id, kind: 'location', location: {lat, lng, accuracy_m, captured_at, label, source} }
 *   { id, kind: 'record_ref', record_ref: {sample_id} | {photo_id}, label }
 *
 * and, once a file is in storage, `uploaded: {attachment_id, size_bytes}`:
 * the send names it by that. The blobs stay with the outbox entry until the
 * message is sent, so a file the server asks for again (`upload_again`) can
 * be put up again.
 */

export const MAX_ATTACHMENTS = 10;
export const MAX_MEDIA_BYTES = 10 * 1024 * 1024;
export const MAX_AUDIO_SECONDS = 300;
/** A photograph's longer side, and its thumbnail's, as the phone sends them. */
export const IMAGE_SIDE = 2048;
export const THUMB_SIDE = 320;
export const MAX_LABEL = 120;

/** The two voice-note types chat keeps; '' for any other. */
export function chatAudioType(type, filename = '') {
  const base = baseAudioType(type, filename);
  return base === 'audio/webm' || base === 'audio/mp4' ? base : '';
}

/** {width, height} of a picture scaled to fit `side` on its longer side, never up. */
export function fitWithin(width, height, side) {
  const w = Math.max(1, Math.round(Number(width) || 1));
  const h = Math.max(1, Math.round(Number(height) || 1));
  const scale = Math.min(1, side / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

let counter = 0;
/** A draft attachment's own id (never sent: the server's is attachment_id). */
export function draftId() {
  counter += 1;
  return `a${Date.now().toString(36)}${counter}`;
}

async function decode(file) {
  // Upright by the photograph's EXIF orientation, as an <img> shows it.
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      /* an older browser: through an <img> below */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await (img.decode ? img.decode() : new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    }));
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function encode(source, width, height, quality) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(source, 0, 0, width, height);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))), 'image/jpeg', quality);
  });
}

/**
 * A photograph as chat sends it: at most IMAGE_SIDE on its longer side and
 * re-encoded as JPEG by a canvas, which carries no EXIF -- so no GPS
 * position travels into a conversation (a place is its own attachment,
 * sent on purpose) -- with a THUMB_SIDE thumbnail beside it.
 * Resolves the draft attachment; rejects for a file that is not a picture.
 */
export async function prepareImage(file, { copy = false } = {}) {
  const source = await decode(file);
  const natural = { width: source.width || source.naturalWidth, height: source.height || source.naturalHeight };
  const big = fitWithin(natural.width, natural.height, IMAGE_SIDE);
  const small = fitWithin(natural.width, natural.height, THUMB_SIDE);
  const [blob, thumb] = await Promise.all([encode(source, big.width, big.height, 0.86),
    encode(source, small.width, small.height, 0.8)]);
  source.close?.();
  return { id: draftId(), kind: 'image', blob, thumb, media_type: 'image/jpeg', width: big.width,
    height: big.height, ...(copy ? { copy: true } : {}) };
}

/** A recorded or picked voice note as a draft attachment, or null for a type chat does not keep. */
export function audioAttachment(note, { recordedVia = null } = {}) {
  const type = chatAudioType(note?.contentType || note?.blob?.type);
  if (!type || !note?.blob?.size) return null;
  const seconds = Number(note.durationS);
  return { id: draftId(), kind: 'audio', blob: note.blob, media_type: type,
    duration_s: Number.isFinite(seconds) && seconds > 0 ? Math.min(MAX_AUDIO_SECONDS, Math.round(seconds * 10) / 10) : null,
    recorded_via: recordedVia };
}

/** A place as a draft attachment: here (`device`), a pin, or a record's. */
export function locationAttachment({ lat, lng, accuracy_m = null, captured_at = null, label = '', source }) {
  const clean = String(label || '').replace(/\s+/g, ' ').trim().slice(0, MAX_LABEL);
  return { id: draftId(), kind: 'location',
    location: { lat: Number(lat), lng: Number(lng), accuracy_m: accuracy_m == null ? null : Number(accuracy_m),
      captured_at: captured_at || null, label: clean || null, source } };
}

/** A reference to a collection or a photograph, by id: never a copy. */
export function recordAttachment({ sample_id = null, photo_id = null, label = '', thumbnail = null }) {
  return { id: draftId(), kind: 'record_ref', record_ref: sample_id ? { sample_id } : { photo_id },
    label: label || '', thumbnail };
}

/** What the send says of one attachment (routes/chat.py _parse_attachments). */
export function wireAttachment(item) {
  if (item.kind === 'location') return { location: item.location };
  if (item.kind === 'record_ref') return { record_ref: item.record_ref };
  return {
    attachment_id: item.uploaded.attachment_id,
    size_bytes: item.uploaded.size_bytes,
    ...(item.kind === 'audio' && item.duration_s ? { duration_s: item.duration_s } : {}),
    ...(item.kind === 'audio' && item.recorded_via ? { recorded_via: item.recorded_via } : {}),
  };
}

/** Whether a draft attachment still has to go up before the message can be sent. */
export const needsUpload = (item) => (item.kind === 'image' || item.kind === 'audio') && !item.uploaded;

/**
 * Put one photograph or voice note in storage: POST .../uploads, then the
 * PUTs (the type is signed into them). Resolves `{attachment_id, size_bytes}`;
 * rejects as the request that failed did.
 */
export async function uploadAttachment(client, channelId, item, put = putObject) {
  const thumb = item.kind === 'image' && item.thumb ? item.thumb : null;
  const made = (await client.post(`/chat/channels/${channelId}/uploads`, {
    kind: item.kind, media_type: item.media_type, with_thumbnail: !!thumb,
  })).data;
  await put(made.presign_url, item.blob, made.media_type || item.media_type);
  if (thumb && made.thumb_presign_url) await put(made.thumb_presign_url, thumb, 'image/jpeg');
  return { attachment_id: made.attachment_id, size_bytes: item.blob.size };
}
